import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Textarea } from '../../components/ui/Input';
import type { ContentItem } from '../../api/content';
import { useFinishLinkedInReview, usePatchContentItem } from '../../hooks/useContent';
import { TYPE_LABELS } from './content-labels';

const PERSON_LABELS: Record<string, string> = {
  patrick: 'Patrick Dalvinck',
  evrard: "Evrard t'Serstevens",
  sequesto: 'SEQUESTO',
};

/**
 * Review panel for LinkedIn posts. Shows the generated post on the left and an
 * editable copy on the right. When the reviewer clicks "Finished", their
 * rewrite is saved as the post body AND flagged as a voice sample, so the
 * generator learns to imitate how this person actually writes.
 */
export function LinkedInReviewPanel({ item }: { item: ContentItem }) {
  const navigate = useNavigate();
  const location = useLocation();
  const finish = useFinishLinkedInReview();
  const patchItem = usePatchContentItem();

  const original = item.body ?? '';
  const [draft, setDraft] = useState(original);
  const [done, setDone] = useState<string | null>(null);
  const [published, setPublished] = useState(item.status === 'published');

  const meta = (item.metadata ?? {}) as Record<string, unknown>;
  const person = typeof meta.person === 'string' ? meta.person : null;
  const personLabel = person ? (PERSON_LABELS[person] ?? person) : null;
  const score = meta.score as { overall_score?: number } | undefined;
  const hook = typeof meta.hook === 'string' ? meta.hook : null;
  const cta = typeof meta.cta === 'string' ? meta.cta : null;
  const angle = typeof meta.angle === 'string' ? meta.angle : null;

  const dirty = draft.trim() !== original.trim();

  function handleBack() {
    if (location.state && typeof location.state === 'object' && 'fromLibrary' in location.state) {
      navigate(-1);
      return;
    }
    navigate('/marketing/content/linkedin');
  }

  // No LinkedIn API to read back what was posted — the author posts it manually
  // and marks it published here, which closes the loop (archives the unchosen
  // variations and feeds this post into duplicate-avoidance).
  function handleMarkPublished() {
    patchItem.mutate(
      { id: item.id, updates: { status: 'published' } },
      { onSuccess: () => setPublished(true) },
    );
  }

  function handleFinish() {
    if (!draft.trim()) return;
    finish.mutate(
      { contentItemId: item.id, editedText: draft },
      {
        onSuccess: (result) => {
          setDone(result.review?.message ?? 'Saved. This version is now one of the author\'s voice examples.');
        },
      },
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-6 py-8 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Button variant="ghost" size="sm" onClick={handleBack}>Back</Button>
        <div className="flex items-center gap-2">
          <Badge variant="info">{TYPE_LABELS[item.type] ?? item.type}</Badge>
          {personLabel && <Badge variant="default">{personLabel}</Badge>}
          {typeof score?.overall_score === 'number' && (
            <Badge variant="success">Score {score.overall_score}</Badge>
          )}
        </div>
      </div>

      <div>
        <h1 className="text-heading-sm font-semibold text-text-primary">Review LinkedIn post</h1>
        <p className="text-body-sm text-text-secondary mt-1">
          Rewrite the post on the right in your own words, then click <strong>Finished</strong>. Your version
          is saved as the post and becomes a voice example the generator imitates next time.
        </p>
        {(hook || cta || angle) && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-body-xs text-text-tertiary">
            {angle && <span><strong>Angle:</strong> {angle}</span>}
            {hook && <span><strong>Hook:</strong> {hook}</span>}
            {cta && <span><strong>CTA:</strong> {cta}</span>}
          </div>
        )}
      </div>

      {done && <Alert variant="success">{done}</Alert>}
      {finish.error instanceof Error && <Alert variant="error">{finish.error.message}</Alert>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="p-4 space-y-2">
          <h3 className="text-body-md font-medium text-text-primary">Original (generated)</h3>
          <pre className="whitespace-pre-wrap break-words text-body-sm text-text-secondary font-sans">
            {original || '—'}
          </pre>
        </Card>

        <Card className="p-4 space-y-2">
          <h3 className="text-body-md font-medium text-text-primary">Your version</h3>
          <Textarea
            aria-label="Your version"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={18}
            disabled={finish.isPending || !!done}
            className="resize-y"
          />
          <div className="text-body-xs text-text-tertiary">{draft.length} characters</div>
        </Card>
      </div>

      {published && <Alert variant="success">Marked as published. This post now counts as "already posted" and its sibling variations were archived.</Alert>}
      {patchItem.error instanceof Error && <Alert variant="error">{patchItem.error.message}</Alert>}

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={handleBack} disabled={finish.isPending}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="secondary"
          onClick={handleMarkPublished}
          disabled={patchItem.isPending || published}
        >
          {published ? 'Published' : patchItem.isPending ? 'Marking…' : 'Mark as published'}
        </Button>
        <Button
          variant="primary"
          onClick={handleFinish}
          disabled={finish.isPending || !!done || !draft.trim() || (!dirty && !original)}
        >
          {finish.isPending ? 'Saving…' : 'Finished'}
        </Button>
      </div>
    </div>
  );
}
